import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { verifyOtp, login } from '../services/auth';
import logo from '../assets/liferx-logo.png';

const OtpVerificationPage = () => {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds
  const [resendDisabled, setResendDisabled] = useState(true);
  const location = useLocation();
  const email = location.state?.email;
  const timerRef = useRef(null);

  // Redirect to login if no email is provided
  useEffect(() => {
    if (!email) {
      window.location.href = '/login';
    }
  }, [email]);

  // Set up countdown timer
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timerRef.current);
          setResendDisabled(false);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    // Enable resend after 30 seconds
    const resendTimer = setTimeout(() => {
      setResendDisabled(false);
    }, 30000);

    return () => {
      clearInterval(timerRef.current);
      clearTimeout(resendTimer);
    };
  }, []);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await verifyOtp(email, otp);
      console.log('OTP verification successful:', response);
      
      // Redirect to dashboard
      window.location.href = '/';
    } catch (err) {
      console.error('OTP verification error:', err);
      setError(
        err.response?.data?.error || 
        'Invalid or expired OTP. Please try again or request a new OTP.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    setError('');
    setResendDisabled(true);

    try {
      await login(email, ''); // Backend should check email and resend OTP
      
      // Reset timer
      setTimeLeft(300);
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timerRef.current);
            setResendDisabled(false);
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
      
      // Disable resend button for 30 seconds
      setTimeout(() => {
        setResendDisabled(false);
      }, 30000);
      
    } catch (err) {
      console.error('Resend OTP error:', err);
      setError(
        err.response?.data?.error || 
        'Unable to resend OTP. Please try again later.'
      );
      setResendDisabled(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <VerificationContainer>
      <VerificationCard>
        <LogoContainer>
          <img src={logo} alt="LifeRx Logo" />
          <h1>LifeRx Portal</h1>
        </LogoContainer>
        
        <h2 style={{ color: 'var(--text-primary, #f0f0f0)' }}>OTP Verification</h2>
        <p style={{ color: 'var(--text-secondary, #aaa)' }}>Enter the 6-digit code sent to your email: {email}</p>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <form onSubmit={handleVerifyOtp}>
          <FormGroup>
            <Label htmlFor="otp">One-Time Password</Label>
            <OtpInput
              type="text"
              id="otp"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, '').substring(0, 6))}
              placeholder="Enter 6-digit OTP"
              maxLength={6}
              required
            />
          </FormGroup>
          
          <TimerContainer>
            <span>OTP expires in: </span>
            <TimerText expired={timeLeft === 0}>{formatTime(timeLeft)}</TimerText>
          </TimerContainer>
          
          <ButtonGroup>
            <SubmitButton type="submit" disabled={loading || otp.length !== 6}>
              {loading ? 'Verifying...' : 'Verify OTP'}
            </SubmitButton>
            
            <ResendButton 
              type="button" 
              disabled={resendDisabled || loading}
              onClick={handleResendOtp}
            >
              Resend OTP
            </ResendButton>
          </ButtonGroup>
        </form>
        
        <BackLink onClick={() => window.location.href = '/login'}>
          &larr; Back to Login
        </BackLink>
        
        <Footer>
          <p>LifeRx Staff Portal - For authorized personnel only</p>
        </Footer>
      </VerificationCard>
    </VerificationContainer>
  );
};

// Styled Components
const VerificationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: var(--bg-primary, #1a1a1a);
`;

const VerificationCard = styled.div`
  width: 100%;
  max-width: 450px;
  padding: 2rem;
  background-color: var(--bg-secondary, #2a2a2a);
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  
  img {
    height: 60px;
    margin-right: 1rem;
    object-fit: contain;
  }
  
  h1 {
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--primary, #6366f1);
    margin: 0;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-secondary, #aaa);
`;

const OtpInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color, #444);
  border-radius: 4px;
  font-size: 1.25rem;
  letter-spacing: 0.25rem;
  text-align: center;
  background-color: var(--bg-input, white);
  color: var(--text-primary, #f0f0f0);
  
  @media (prefers-color-scheme: light) {
    background-color: white;
    color: #333;
    border-color: #ddd;
  }
  
  @media (prefers-color-scheme: dark) {
    background-color: var(--bg-input, #333);
    color: var(--text-primary, #f0f0f0);
    border-color: var(--border-color, #444);
  }
  
  &:focus {
    outline: none;
    border-color: var(--primary, #6366f1);
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
  }
`;

const TimerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  color: var(--text-secondary, #aaa);
`;

const TimerText = styled.span`
  margin-left: 0.5rem;
  font-weight: 600;
  color: ${props => props.expired ? 'var(--danger, #f87171)' : 'var(--success, #10b981)'};
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background-color: var(--primary, #6366f1);
  color: var(--text-on-primary, white);
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: var(--primary-dark, #4f46e5);
  }
  
  &:disabled {
    background-color: var(--disabled, #4b5563);
    cursor: not-allowed;
  }
`;

const ResendButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background-color: transparent;
  color: var(--primary, #6366f1);
  border: 1px solid var(--primary, #6366f1);
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: rgba(99, 102, 241, 0.1);
  }
  
  &:disabled {
    color: var(--disabled, #6b7280);
    border-color: var(--border-color, #444);
    cursor: not-allowed;
  }
`;

const BackLink = styled.button`
  display: block;
  margin: 1.5rem auto 0;
  background: none;
  border: none;
  color: var(--primary, #6366f1);
  font-size: 0.875rem;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ErrorMessage = styled.div`
  padding: 0.75rem;
  margin-bottom: 1.5rem;
  background-color: rgba(239, 68, 68, 0.1);
  border: 1px solid var(--danger, #f87171);
  border-radius: 4px;
  color: var(--danger, #f87171);
  font-size: 0.875rem;
`;

const Footer = styled.div`
  margin-top: 2rem;
  text-align: center;
  font-size: 0.75rem;
  color: var(--text-secondary, #aaa);
`;

export default OtpVerificationPage;